/**
 * webFontCss — @font-face CSS builder for self-hosted web fonts
 *
 * Pure string builder (no DOM) shared byte-identically by:
 * - ensureWebFontsLoaded (client <style> injection)
 * - V2WebShellHTMLService (published-HTML head bake, via leaf import)
 *
 * Emits, per asset:
 * - one real @font-face per face (font-display: swap)
 * - one metric-adjusted "<family> Fallback" face over local() system fonts
 *   (size-adjust / ascent-override / descent-override / line-gap-override)
 *   — the piece that makes the swap zero-CLS.
 *
 * @module font-config/utils
 */

import type { WebFontAsset, WebFontFace } from '../types/fontConfig.types';
import {
  WEB_FONT_FALLBACK_LOCAL_SOURCES,
  webFontFallbackFamily,
} from '../data/googleFontCatalog';

/**
 * Defense-in-depth: family names land inside quoted CSS strings; strip
 * anything that could break out. Curated names are repo-controlled and
 * uploads are sanitized at ingest, so legit names pass through unchanged.
 */
function sanitizeFamily(family: string): string {
  return family.replace(/['"\\<>{};()]/g, '');
}

/** Legit GCS URLs never contain quotes/parens — stripping them blocks url() breakout. */
function sanitizeUrl(url: string): string {
  return url.replace(/['"\\()<>]/g, '');
}

function pct(value: number): string {
  return `${+(value * 100).toFixed(2)}%`;
}

/**
 * The face worth preloading: primary weight, normal style; among subset
 * faces prefer the unrestricted one, then latin (first paint is latin-heavy).
 */
export function getPreloadFace(asset: WebFontAsset): WebFontFace | null {
  const primary = asset.faces.filter(
    (face) => face.weight === asset.primaryWeight && face.style === 'normal',
  );
  const candidates = primary.length > 0 ? primary : asset.faces;
  return (
    candidates.find((face) => !face.unicodeRange) ??
    candidates.find((face) => face.unicodeRange?.startsWith('U+0000')) ??
    candidates[0] ?? null
  );
}

/**
 * Build the full @font-face stylesheet for a set of resolved assets.
 */
export function buildWebFontFaceCss(assets: WebFontAsset[]): string {
  const rules: string[] = [];

  for (const asset of assets) {
    const family = sanitizeFamily(asset.family);

    for (const face of asset.faces) {
      const unicodeRange = face.unicodeRange
        ? `unicode-range:${face.unicodeRange.replace(/[^0-9A-Fa-f+,\-U? ]/g, '')};`
        : '';
      rules.push(
        `@font-face{font-family:'${family}';font-style:${face.style === 'italic' ? 'italic' : 'normal'};` +
          `font-weight:${face.weight};font-display:swap;` +
          `src:url('${sanitizeUrl(face.woff2)}') format('woff2');${unicodeRange}}`,
      );
    }

    const metrics = asset.fallbackMetrics;
    const locals = WEB_FONT_FALLBACK_LOCAL_SOURCES[asset.category]
      .map((name) => `local('${name}')`)
      .join(',');
    rules.push(
      `@font-face{font-family:'${sanitizeFamily(webFontFallbackFamily(asset.family))}';` +
        `src:${locals};` +
        `ascent-override:${pct(metrics.ascentOverride)};` +
        `descent-override:${pct(metrics.descentOverride)};` +
        `line-gap-override:${pct(metrics.lineGapOverride)};` +
        `size-adjust:${pct(metrics.sizeAdjust)};}`,
    );
  }

  return rules.join('\n');
}
